/**
 * <s-carousel> — a horizontally-scrollable "swim lane" that shows multiple items at once.
 *
 * UI-only: it wires the optional prev/next buttons and toggles their `disabled` state at the
 * start/end of the scroll range. The track is a native scroll-snap container, so scrolling,
 * touch/trackpad swiping, and keyboard focus all work without JS.
 *
 * Optional autoplay (`data-autoplay="<seconds>"`): advances one item at a time and loops back
 * to the start at the end. Autoplay pauses while the pointer is over the carousel, while focus
 * is inside it, after a touch, while it is offscreen or the tab is hidden, and never runs under
 * prefers-reduced-motion. When autoplaying, the arrows also step one item and wrap at the ends.
 *
 * Expected DOM (rendered by blocks/carousel.liquid):
 *   <s-carousel [data-autoplay]>
 *     [<button data-carousel-prev> / <button data-carousel-next>]
 *     <div data-carousel-track> …items… </div>
 *   </s-carousel>
 */
class SCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-carousel-track]");
    this.prevButton = this.querySelector("[data-carousel-prev]");
    this.nextButton = this.querySelector("[data-carousel-next]");

    if (!this.track) return;

    this.autoplaySeconds = Number(this.dataset.autoplay) || 0;
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.looping = this.autoplaySeconds > 0;
    this.pauseReasons = new Set();

    this.onPrev = () => (this.looping ? this.step(-1) : this.scrollByPage(-1));
    this.onNext = () => (this.looping ? this.step(1) : this.scrollByPage(1));
    this.onScroll = () => this.updateControls();

    this.prevButton?.addEventListener("click", this.onPrev);
    this.nextButton?.addEventListener("click", this.onNext);
    this.track.addEventListener("scroll", this.onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);

    if (this.looping) this.setupAutoplay();

    this.updateControls();
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener("click", this.onPrev);
    this.nextButton?.removeEventListener("click", this.onNext);
    this.track?.removeEventListener("scroll", this.onScroll);
    this.resizeObserver?.disconnect();
    this.teardownAutoplay();
  }

  setupAutoplay() {
    const pause = (reason) => () => {
      this.pauseReasons.add(reason);
      this.syncTimer();
    };
    const resume = (reason) => () => {
      this.pauseReasons.delete(reason);
      this.syncTimer();
    };

    this.listeners = [
      [this, "pointerenter", pause("hover")],
      [this, "pointerleave", resume("hover")],
      [this, "focusin", pause("focus")],
      [this, "focusout", (event) => {
        if (!this.contains(event.relatedTarget)) resume("focus")();
      }],
      // A touch means the shopper is driving; stop for good this visit.
      [this.track, "touchstart", pause("touch")],
      [document, "visibilitychange", () => {
        if (document.hidden) pause("hidden")();
        else resume("hidden")();
      }],
      [this.reducedMotion, "change", () => this.syncTimer()],
    ];
    for (const [target, type, handler] of this.listeners) {
      target.addEventListener(type, handler, { passive: true });
    }

    this.visibilityObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) resume("offscreen")();
      else pause("offscreen")();
    });
    this.visibilityObserver.observe(this);
  }

  teardownAutoplay() {
    window.clearInterval(this.timer);
    this.timer = undefined;
    for (const [target, type, handler] of this.listeners ?? []) {
      target.removeEventListener(type, handler);
    }
    this.listeners = [];
    this.visibilityObserver?.disconnect();
  }

  syncTimer() {
    const shouldRun = this.pauseReasons.size === 0 && !this.reducedMotion.matches;
    if (shouldRun && !this.timer) {
      this.timer = window.setInterval(() => this.step(1), this.autoplaySeconds * 1000);
    } else if (!shouldRun && this.timer) {
      window.clearInterval(this.timer);
      this.timer = undefined;
    }
  }

  /** Width of one item plus the gap, i.e. the distance between snap points. */
  itemStride() {
    const items = this.track.children;
    if (items.length < 2) return this.track.clientWidth;
    return Math.abs(items[1].offsetLeft - items[0].offsetLeft);
  }

  /**
   * Move one item in the given direction, wrapping at either end.
   * @param {number} direction -1 for previous, 1 for next.
   */
  step(direction) {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (maxScroll <= 1) return;
    const position = Math.abs(this.track.scrollLeft);
    const sign = getComputedStyle(this.track).direction === "rtl" ? -1 : 1;

    let target = position + this.itemStride() * direction;
    if (direction > 0 && position >= maxScroll - 1) target = 0;
    if (direction < 0 && position <= 1) target = maxScroll;

    this.track.scrollTo({ left: Math.min(Math.max(target, 0), maxScroll) * sign, behavior: "smooth" });
  }

  /**
   * Scroll the track by ~80% of its visible width in the given direction.
   * @param {number} direction -1 for previous, 1 for next.
   */
  scrollByPage(direction) {
    const amount = this.track.clientWidth * 0.8 * direction;
    this.track.scrollBy({ left: amount, behavior: "smooth" });
  }

  /** Disable prev/next at the ends, unless the carousel loops. */
  updateControls() {
    if (!this.prevButton && !this.nextButton) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const position = Math.abs(this.track.scrollLeft);
    const atStart = position <= 1;
    const atEnd = position >= maxScroll - 1;
    const overflowing = maxScroll > 1;

    if (this.prevButton) this.prevButton.disabled = !overflowing || (!this.looping && atStart);
    if (this.nextButton) this.nextButton.disabled = !overflowing || (!this.looping && atEnd);
  }
}

if (!customElements.get("s-carousel")) {
  customElements.define("s-carousel", SCarousel);
}
